import React from "react";
import { AlertTriangle, ChevronDown, ExternalLink, MapPin, PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { eventColor, timeAgo } from "../utils/nws";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";

export const RadarAlertButton = ({ alert, index, onSelect, testIdPrefix = "radar-alert-item" }) => {
  const color = eventColor(alert.event);
  return (
    <button
      type="button"
      onClick={() => onSelect(alert)}
      data-testid={`${testIdPrefix}-${index}`}
      className="group w-full border-l-4 px-3 py-3 text-left transition-[background-color,transform] duration-200 hover:bg-white/[0.07] focus-visible:bg-white/[0.07] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-cyan-400"
      style={{ borderColor: color.fill }}
    >
      <div className="flex items-start justify-between gap-2">
        <span className="text-[13px] font-bold leading-tight text-slate-100 group-hover:text-white">{alert.event}</span>
        <span className="mono shrink-0 text-[10px] text-slate-500">{timeAgo(alert.sent)}</span>
      </div>
      <div className="mt-1 line-clamp-2 text-[11px] text-slate-300">{alert.areaDesc || "Area details unavailable"}</div>
      <div className="mono mt-1.5 flex items-center gap-1 text-[10px] font-semibold uppercase text-cyan-300">
        View alert <ExternalLink className="h-2.5 w-2.5" />
      </div>
    </button>
  );
};

export const RadarAlertPanel = ({ alerts, filtered, filter, setFilter, loading, lastUpdated, counts, selectedAlert, setSelectedAlert, collapsed, onToggle, radarReady, relayRunning }) => (
  <>
    <aside
      className={`hidden shrink-0 flex-col border-r border-white/10 bg-[rgba(3,6,16,0.94)] backdrop-blur-md transition-[width] duration-300 md:flex ${collapsed ? "w-14" : "w-[300px]"}`}
      data-testid="radar-alert-sidebar"
    >
      {collapsed ? (
        <button
          type="button"
          onClick={onToggle}
          data-testid="radar-alert-panel-expand-button"
          aria-label="Expand NWS alerts panel"
          title="Expand NWS alerts"
          className="flex h-full w-full flex-col items-center gap-3 border-0 bg-transparent px-2 py-4 text-slate-300 transition-[background-color,color] hover:bg-white/[0.06] hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-cyan-400"
        >
          <PanelLeftOpen className="h-5 w-5 text-cyan-300" />
          <span className="mono rounded bg-red-600 px-1.5 py-0.5 text-[10px] font-bold text-white">{alerts.length}</span>
          <span className="mono [writing-mode:vertical-rl] text-[10px] font-semibold uppercase tracking-widest">Storm Desk</span>
        </button>
      ) : (
      <>
      <div className="border-b border-white/5 px-4 pb-3 pt-4">
        <div className="mb-2 flex items-center justify-between">
          <div className="mono flex items-center gap-2 text-[11px] uppercase tracking-widest text-slate-300">
            <span className="text-blue-300">◆</span> Storm Desk Panel
          </div>
          <div className="flex items-center gap-1.5">
            <span data-testid="radar-alert-count" className="mono rounded-full border border-red-500/30 bg-red-500/15 px-2 py-0.5 text-[10px] font-semibold text-red-200">{alerts.length}</span>
            <button
              type="button"
              onClick={onToggle}
              data-testid="radar-alert-panel-collapse-button"
              aria-label="Collapse NWS alerts panel"
              title="Collapse NWS alerts"
              className="grid h-7 w-7 place-items-center rounded border border-white/10 text-slate-400 transition-[background-color,color] hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400"
            >
              <PanelLeftClose className="h-4 w-4" />
            </button>
          </div>
        </div>
        <div className="mb-3 grid gap-1.5 border-y border-white/5 py-2 text-[10px]">
          <div className="flex items-center justify-between"><span className="text-slate-500">Weather status</span><span className={`mono font-bold uppercase ${counts.tor + counts.svr + counts.ff > 0 ? "text-amber-300" : "text-emerald-300"}`}>{counts.tor + counts.svr + counts.ff > 0 ? "Warnings active" : "Monitoring"}</span></div>
          <div className="flex items-center justify-between"><span className="text-slate-500">Radar source</span><span className={`mono font-bold uppercase ${radarReady ? "text-emerald-300" : "text-slate-600"}`}>{radarReady ? "Ready" : "Loading"}</span></div>
          <div className="flex items-center justify-between"><span className="text-slate-500">Storm Desk Live</span><span className={`mono font-bold uppercase ${relayRunning ? "text-red-300" : "text-slate-600"}`}>{relayRunning ? "On air" : "Off air"}</span></div>
        </div>
        <div className="mono mb-2 flex items-center gap-2 text-[10px] uppercase tracking-widest text-slate-400"><AlertTriangle className="h-3.5 w-3.5 text-red-400" /> Active alerts</div>
        <div className="flex flex-wrap items-center gap-1.5">
          {counts.tor > 0 && <span className="mono rounded bg-red-600 px-2 py-0.5 text-[10px] font-bold text-white">{counts.tor} TOR</span>}
          {counts.svr > 0 && <span className="mono rounded bg-amber-500 px-2 py-0.5 text-[10px] font-bold text-black">{counts.svr} SVR</span>}
          {counts.ff > 0 && <span className="mono rounded bg-emerald-500 px-2 py-0.5 text-[10px] font-bold text-black">{counts.ff} FF</span>}
          {alerts.length === 0 && <span className="mono text-[10px] font-bold text-emerald-300">ALL CLEAR</span>}
        </div>
      </div>

      <div className="border-b border-white/5 px-3 py-2">
        <div className="relative">
          <select
            value={filter}
            onChange={(event) => setFilter(event.target.value)}
            data-testid="radar-alert-filter"
            aria-label="Filter active weather alerts"
            className="w-full appearance-none rounded-md border border-white/10 bg-black/40 px-3 py-1.5 pr-8 text-[12px] text-slate-100 focus:border-cyan-400 focus:outline-none"
          >
            <option>All</option>
            <option>Tornado</option>
            <option>Severe Thunderstorm</option>
            <option>Flash Flood</option>
            <option>Watch</option>
          </select>
          <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        </div>
      </div>

      <div className="flex-1 divide-y divide-white/5 overflow-y-auto">
        {loading && <div data-testid="radar-alert-loading" className="mono px-4 py-4 text-[12px] text-slate-500">Loading NWS alerts…</div>}
        {!loading && filtered.length === 0 && (
          <div data-testid="radar-alert-empty" className="px-4 py-6 text-center">
            <div className="mono text-sm font-bold text-emerald-300">ALL CLEAR</div>
            <div className="mt-1 text-[11px] text-slate-500">No active {filter !== "All" ? filter.toLowerCase() : ""} alerts.</div>
          </div>
        )}
        {filtered.map((alert, index) => <RadarAlertButton key={`${alert.id || alert.event}-${index}`} alert={alert} index={index} onSelect={setSelectedAlert} />)}
      </div>

      {lastUpdated && (
        <div data-testid="radar-alert-updated-time" className="mono border-t border-white/5 px-4 py-2 text-[10px] text-slate-500">
          Updated {lastUpdated.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
        </div>
      )}
      </>
      )}
    </aside>
    <RadarAlertDialog selectedAlert={selectedAlert} setSelectedAlert={setSelectedAlert} />
  </>
);

export const RadarAlertDialog = ({ selectedAlert, setSelectedAlert }) => (
  <>
    <Dialog open={Boolean(selectedAlert)} onOpenChange={(open) => !open && setSelectedAlert(null)}>
      <DialogContent data-testid="radar-alert-dialog" className="max-h-[85vh] max-w-2xl overflow-y-auto border-white/10 bg-[#070b15] text-slate-100 shadow-2xl">
        <DialogHeader>
          <div className="mono mb-1 flex items-center gap-2 text-[11px] uppercase tracking-widest text-red-300">
            <AlertTriangle className="h-4 w-4" /> Official NWS Alert
          </div>
          <DialogTitle data-testid="radar-alert-dialog-title" className="display text-2xl font-black uppercase text-white">{selectedAlert?.event || "Weather Alert"}</DialogTitle>
          <DialogDescription data-testid="radar-alert-dialog-area" className="flex items-start gap-2 text-sm text-slate-300">
            <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-cyan-400" /> {selectedAlert?.areaDesc || "Affected area unavailable"}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 text-sm leading-relaxed text-slate-300">
          {selectedAlert?.headline && <p data-testid="radar-alert-dialog-headline" className="border-l-2 border-red-500 pl-3 font-semibold text-slate-100">{selectedAlert.headline}</p>}
          {selectedAlert?.description && <p data-testid="radar-alert-dialog-description" className="whitespace-pre-line">{selectedAlert.description}</p>}
          {selectedAlert?.instruction && (
            <div data-testid="radar-alert-dialog-instructions" className="border border-amber-400/20 bg-amber-400/[0.07] p-3 text-amber-50">
              <div className="mono mb-1 text-[10px] font-bold uppercase tracking-widest text-amber-300">Safety Instructions</div>
              <p className="whitespace-pre-line">{selectedAlert.instruction}</p>
            </div>
          )}
          {selectedAlert?.id?.startsWith("https://") && (
            <a
              href={selectedAlert.id}
              target="_blank"
              rel="noreferrer noopener"
              data-testid="radar-alert-official-link"
              className="inline-flex w-fit items-center gap-2 text-sm font-semibold text-cyan-300 hover:text-cyan-200"
            >
              Open on weather.gov <ExternalLink className="h-4 w-4" />
            </a>
          )}
        </div>
      </DialogContent>
    </Dialog>
  </>
);